<template>
  <section class="hero">
    <div
      class="hero__bg"
      aria-hidden="true">
      <div class="hero__bg-gradient" />
      <div class="noise-overlay" />
    </div>

    <div class="hero__inner">
      <!-- Titre -->
      <h1 class="hero__title">
        {{ t('hero.titleLine1') }}<br />
        {{ t('hero.titleLine2') }}
      </h1>

      <!-- Sous-titre -->
      <p class="hero__subtitle">
        {{ t('hero.subtitle') }}
      </p>

      <!-- CTA principal -->
      <div class="hero__cta animate-fade-up delay-300">
        <NuxtLink
          :to="localePath('/register')"
          class="btn btn-primary btn-lg">
          {{ t('hero.ctaPrimary') }}
        </NuxtLink>
        <p class="hero__social-count">
          <span>👉</span>
          {{ t('hero.socialCount') }}
        </p>
      </div>

      <!-- Séparateur + liens secondaires -->
      <div class="hero__secondary animate-fade-up delay-400">
        <div class="hero__divider" />
        <p class="hero__login">{{ t('hero.alreadyMember') }}</p>
        <NuxtLink
          :to="localePath('/login')"
          class="hero__login-link"
          >{{ t('hero.login') }}</NuxtLink
        >
        <div class="hero__divider" />
        <NuxtLink
          to="https://edu.prepeers.co/"
          external
          target="_blank"
          rel="noopener noreferrer"
          class="hero__school-link"
          >{{ t('hero.schoolLink') }}</NuxtLink
        >
      </div>
    </div>
  </section>
</template>

<script setup>
const { t } = useI18n()
const localePath = useLocalePath()
</script>

<style scoped>
.hero {
  position: relative;
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding-top: var(--header-height);
}

.hero__bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

/* Fond rouge/bordeaux à gauche → bleu/violet à droite, comme le jpg */
.hero__bg-gradient {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 80% at 15% 50%, rgba(120, 20, 20, 0.85) 0%, transparent 70%),
    radial-gradient(ellipse 60% 80% at 85% 50%, rgba(30, 20, 80, 0.85) 0%, transparent 70%), #07070f;
}

.hero__inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-6);
  padding: var(--space-20) var(--container-pad);
  max-width: var(--container-md);
  margin-inline: auto;
}

.hero__social-proof {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: var(--radius-full);
  padding: 0.35em 1.1em;
}

.hero__social-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--accent-green);
  box-shadow: 0 0 8px var(--accent-green);
  flex-shrink: 0;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

.hero__title {
  font-size: clamp(1.75rem, 4vw, 2.25rem); /* max ~36px */
  font-weight: 800;
  line-height: var(--leading-tight);
  letter-spacing: var(--tracking-tight);
  color: var(--text-primary);
}

.hero__subtitle {
  font-size: var(--text-base);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
  max-width: 480px;
}

.hero__cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-6);
  margin-bottom: var(--space-4);
  margin-top: var(--space-8);
}

.hero__social-count {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.hero__secondary {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-6);
  width: 100%;
  max-width: 300px;
}

.hero__divider {
  width: 100%;
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
}

.hero__login {
  font-size: var(--text-base);
  color: var(--text-muted);
  margin-bottom: calc(-1 * var(--space-2));
}

.hero__login-link,
.hero__school-link {
  color: var(--text-secondary);
  text-decoration: none;
  font-weight: 500;
  font-size: var(--text-base);
  transition: color var(--duration-fast) var(--ease-out);
}

.hero__login-link:hover,
.hero__school-link:hover {
  color: var(--text-primary);
}

@media (max-width: 640px) {
  .hero__title {
    font-size: clamp(1.5rem, 6vw, 1.875rem);
  }
}
</style>
